import { VAlign } from '../../../core/enums';
import makeAtom from '../../../core/makeAtom';
import makeDLSMapObj from '../../../core/makeDLSMapObj';
import type { Theme } from '../../../core/types';
import styles from './index.module.less';

enum Size {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

interface ContainerProps {
  children?: React.ReactNode;
  className?: string;
  Component?: keyof React.ReactHTML | React.ElementType;
  theme?: Theme;
  $fluid?: boolean;
  $responsive?: boolean;
  qeId?: string;
}

const upperFirst = (str = '') => str[0].toUpperCase() + str.slice(1);

export const Container = makeAtom<ContainerProps>({
  name: 'Container',
  rootClass: styles.container,
  styles,
  modifiersToDLSMap: makeDLSMapObj(styles, 'container', [
    'fluid',
    'responsive',
  ]),
});

export interface RowProps {
  id?: string;
  role?: string;
  children?: React.ReactNode;
  className?: string;
  Component?: keyof React.ReactHTML | React.ElementType;
  theme?: Theme;
  $sm?: VAlign;
  $md?: VAlign;
  $lg?: VAlign;
  qeId?: string;
}

export const Row = makeAtom<RowProps>({
  name: 'Row',
  rootClass: styles.row,
  styles,
  modifiersToDLSMap: makeDLSMapObj(
    styles,
    'row',
    Object.values(Size).map((size) => [size, Object.values(VAlign)])
  ),
});

type Position = 'first' | 'last';

export type ColumnSize =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | '11'
  | '12';

export interface ColumnProps {
  children?: React.ReactNode;
  className?: string;
  theme?: Theme;
  Component?: keyof React.ReactHTML | React.ElementType;
  qeId?: string;
  role?: string;
  id?: string;
  // sm
  $sm?: ColumnSize;
  $smPull?: ColumnSize;
  $smPush?: ColumnSize;
  $smOffset?: ColumnSize;
  $smPosition?: Position;
  $smAlign?: VAlign;

  // md
  $md?: ColumnSize;
  $mdPull?: ColumnSize;
  $mdPush?: ColumnSize;
  $mdOffset?: ColumnSize;
  $mdPosition?: Position;
  $mdAlign?: VAlign;

  // lg
  $lg?: ColumnSize;
  $lgPull?: ColumnSize;
  $lgPush?: ColumnSize;
  $lgOffset?: ColumnSize;
  $lgPosition?: Position;
  $lgAlign?: VAlign;
}

export const Column = makeAtom<ColumnProps>({
  name: 'Column',
  rootClass: styles.col,
  styles,
  modifiersToDLSMap: (() => {
    const sizes = Object.values(Size);
    const columnsNumber = [...Array(13).keys()]; // from 0 to 12

    const matchedNames = makeDLSMapObj(styles, 'col', [
      // size, e.g. $lg={1} -> colLg1, $lg={2} -> colLg2, etc
      ...sizes.map((size: string) => [size, columnsNumber.map(String)]),

      // size + Pull, e.g. $lgPull={1} -> colLgPull1, $lgPull={2} -> colLgPull2, etc
      ...sizes.map((size: string) => [
        `${size}Pull`,
        columnsNumber.map(String),
      ]),

      // size + Push, e.g. $lgPush={1} -> colLgPush1, $lgPush={2} -> colLgPush2, etc
      ...sizes.map((size: string) => [
        `${size}Push`,
        columnsNumber.map(String),
      ]),

      // size + Offset, e.g. $lgOffset={1} -> colLgOffset1, $lgOffset={2} -> colLgOffset2, etc
      ...sizes.map((size: string) => [
        `${size}Offset`,
        columnsNumber.map(String),
      ]),
    ]);

    // not matching name, e.g. $mdPosition="first" -> .colMdFirst
    const notMatchedNames = sizes.reduce((obj, size): any => {
      return {
        ...obj,
        [`$${size}Position`]: ['first', 'last'].reduce(
          (obj, value) => ({
            ...obj,
            [value]: [styles[`col${upperFirst(size)}${upperFirst(value)}`]],
          }),
          {}
        ),
        [`$${size}Align`]: Object.keys(VAlign)
          .map((x) => x.toLowerCase())
          .reduce(
            (obj, value) => ({
              ...obj,
              [value]: [styles[`col${upperFirst(size)}${upperFirst(value)}`]],
            }),
            {}
          ),
      };
    }, {});

    return {
      ...matchedNames,
      ...notMatchedNames,
    };
  })(),
});
